'use client';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { gatewayClient } from '../../api';
import { useErrorMessage } from '../../hooks/useErrorMessage';
import { KycStartResponse, KycStatus } from '../../types/kyc';
import { KYC_POLL } from '@/constants';
import styles from './OtpStepUpModal.module.css';

// Risk-band step-up at checkout (docs/features/risk-fraud.md §3.3): the
// backend rejected the buy with RISK_ORDER_KYC_REQUIRED. The shopper verifies
// on Ondato's hosted flow, opened in a NEW tab so this cart tab survives -
// with "only mobile" on, a desktop shopper scans a QR and finishes on the
// phone, so the redirect back never reaches this tab. The verdict lands on
// our backend by webhook; we poll /user/kyc/status and, once approved,
// onVerified() re-runs the checkout.
export type KycChallenge = {
  kycStatus: KycStatus;
  retryable?: boolean;
};

interface KycStepUpModalProps {
  challenge: KycChallenge;
  onClose: () => void;
  onVerified: () => void;
}

type Phase =
  | 'intro'
  | 'starting'
  | 'waiting'
  | 'approved'
  | 'rejected'
  | 'timeout'
  | 'error';

const TITLE_KEY: Record<Phase, string> = {
  intro: 'title',
  starting: 'title',
  error: 'title',
  waiting: 'waitingTitle',
  timeout: 'waitingTitle',
  approved: 'approvedTitle',
  rejected: 'rejectedTitle',
};

const APPROVED_HANDOFF_MS = 800;

const initialPhase = (challenge: KycChallenge): Phase =>
  challenge.kycStatus === KycStatus.Rejected && challenge.retryable === false
    ? 'rejected'
    : 'intro';

// Opened synchronously inside the click so popup blockers allow it; pointed
// at the flow once the backend hands us the URL. Null when blocked.
const openBlankTab = (): Window | null =>
  typeof window === 'undefined' ? null : window.open('', '_blank');

export const KycStepUpModal: React.FC<KycStepUpModalProps> = ({
  challenge,
  onClose,
  onVerified,
}) => {
  const t = useTranslations('cart.summary.kyc');
  const resolveError = useErrorMessage();

  const [phase, setPhase] = useState<Phase>(() => initialPhase(challenge));
  const [retryable, setRetryable] = useState(challenge.retryable !== false);
  const [flowUrl, setFlowUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closedRef = useRef(false);
  const waitingSinceRef = useRef(0);

  const close = () => {
    closedRef.current = true;
    onClose();
  };

  const startWaiting = () => {
    waitingSinceRef.current = Date.now();
    setPhase('waiting');
  };

  const applyStartResult = (result: KycStartResponse, tab: Window | null) => {
    if (result.kycStatus === KycStatus.Approved) {
      tab?.close();
      setPhase('approved');
      return;
    }
    if (!result.redirectUrl) {
      tab?.close();
      setError(t('timeoutHint'));
      setPhase('error');
      return;
    }
    setFlowUrl(result.redirectUrl);
    if (tab) tab.location.href = result.redirectUrl;
    startWaiting();
  };

  const applyStartError = (err: unknown) => {
    if ((err as { errorCode?: string })?.errorCode === 'KYC_NOT_RETRYABLE') {
      setRetryable(false);
      setPhase('rejected');
      return;
    }
    setError(resolveError(err));
    setPhase('error');
  };

  const handleStart = async () => {
    if (phase === 'starting') return;
    setError(null);
    setPhase('starting');
    const tab = openBlankTab();
    try {
      const result = await gatewayClient.kyc.start();
      if (closedRef.current) {
        tab?.close();
        return;
      }
      applyStartResult(result, tab);
    } catch (err) {
      tab?.close();
      applyStartError(err);
    }
  };

  // Poll while the shopper is on the phone. The backend reads the verdict
  // rows written by Ondato's webhook, so this flips within seconds of it.
  useEffect(() => {
    if (phase !== 'waiting') return;
    let cancelled = false;

    const applyStatus = (status: KycStatus, statusRetryable?: boolean) => {
      if (status === KycStatus.Approved) setPhase('approved');
      else if (status === KycStatus.Rejected) {
        setRetryable(statusRetryable !== false);
        setPhase('rejected');
      } else if (status === KycStatus.None) {
        // Session aborted or expired at Ondato: offer a fresh start.
        setPhase('intro');
      } else if (Date.now() - waitingSinceRef.current > KYC_POLL.maxMs) {
        setPhase('timeout');
      }
    };

    const tick = async () => {
      try {
        const status = await gatewayClient.kyc.status();
        if (!cancelled) applyStatus(status.kycStatus, status.retryable);
      } catch {
        // Transient: keep polling until the budget runs out.
      }
    };

    const interval = setInterval(tick, KYC_POLL.intervalMs);
    void tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [phase]);

  // Approved: brief confirmation, then the caller retries the checkout.
  useEffect(() => {
    if (phase !== 'approved') return;
    const timer = setTimeout(() => {
      if (!closedRef.current) onVerified();
    }, APPROVED_HANDOFF_MS);
    return () => clearTimeout(timer);
  }, [phase, onVerified]);

  const showsIntro = phase === 'intro' || phase === 'starting' || phase === 'error';

  return (
    <div className={styles.overlay} onClick={close}>
      <div className={styles.paper} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t(TITLE_KEY[phase])}</h2>
          <button className={styles.closeButton} onClick={close} aria-label={t('close')}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          {showsIntro && (
            <>
              <p className={styles.description}>{t('intro')}</p>
              <p className={styles.description}>{t('phoneNote')}</p>
              {error && <p className={styles.error}>{error}</p>}
              <button
                className={styles.primaryButton}
                onClick={handleStart}
                disabled={phase === 'starting'}
              >
                {phase === 'starting' ? t('starting') : t('start')}
              </button>
            </>
          )}

          {phase === 'waiting' && (
            <>
              <p className={styles.description}>{t('waitingHint')}</p>
              <p className={styles.description}>{t('pendingReview')}</p>
              {flowUrl && (
                <a
                  className={styles.resendButton}
                  href={flowUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('openFallback')}
                </a>
              )}
            </>
          )}

          {phase === 'timeout' && (
            <>
              <p className={styles.description}>{t('timeoutHint')}</p>
              <button className={styles.primaryButton} onClick={startWaiting}>
                {t('checkAgain')}
              </button>
            </>
          )}

          {phase === 'approved' && <p className={styles.description}>{t('approvedHint')}</p>}

          {phase === 'rejected' && (
            <>
              <p className={styles.description}>
                {retryable ? t('rejectedRetryHint') : t('rejectedFinalHint')}
              </p>
              {retryable ? (
                <button className={styles.primaryButton} onClick={handleStart}>
                  {t('tryAgain')}
                </button>
              ) : (
                <button className={styles.primaryButton} onClick={close}>
                  {t('close')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
